import React, { useEffect, useMemo } from 'react';
import { CountBadge, FlexyTabs, TabStructure } from '@europrocurement/flexy-components';
import { Box } from '@mui/material';
import {
    ActivityTab,
    BillingTab,
    DocumentSignTab,
    DossierSignDetailOverlay,
    DownloadableDocumentsTab,
    RequiredDocumentsStatusAction,
    RequiredDocumentsTab,
    useCurrentDossierCtx,
    useDossierDocumentsCtx,
} from '@europrocurement/l2d-modules';
import { useWebsocketActions, useWebsocketState } from '@europrocurement/l2d-websocket';
import { DOC_TYPE_DOWNLOADABLE, DOC_TYPE_REQUIRED } from '@europrocurement/l2d-domain';
import InformationsTab from '../InformationsTab/InformationsTab';
import { MessageTab } from '../MessageTab/MessageTab';

const DossierBody: React.FunctionComponent = function () {
    const { getMissingDocumentsCountByCategory } = useDossierDocumentsCtx();
    const { dossier } = useCurrentDossierCtx();
    const missingDocumentsCount = getMissingDocumentsCountByCategory({
        categoryId: DOC_TYPE_REQUIRED,
        dossierId: dossier?.id as number,
    });
    const downloadableDocuments = getMissingDocumentsCountByCategory({
        categoryId: DOC_TYPE_DOWNLOADABLE,
        dossierId: dossier?.id as number,
    });
    const topicId = useMemo(
        () => (dossier?.id ? `messagerie_dossier_formalite_${dossier.id}` : null),
        [dossier?.id],
    );

    const { socketOpen, unreadCountByTopic } = useWebsocketState();
    const { getMessageCount } = useWebsocketActions();

    useEffect(() => {
        if (socketOpen && topicId) {
            getMessageCount(topicId);
        }
    }, [socketOpen, topicId, getMessageCount]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (topicId && socketOpen) {
                getMessageCount(topicId);
            }
        }, 50000);

        return () => {
            clearInterval(interval);
        };
    }, [topicId, socketOpen, getMessageCount]);

    const tabs: Array<TabStructure> = [
        {
            tabName: 'details',
            tabTitle: 'Détails du dossier',
            tabPanel: <InformationsTab />,
        },
        {
            tabName: 'required-document',
            tabTitle: (
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    Documents requis{' '}
                    {missingDocumentsCount > 0 && (
                        <CountBadge
                            count={missingDocumentsCount}
                            color="warning"
                            bgVariant="main"
                            contrastText
                        />
                    )}
                </Box>
            ),
            tabPanel: (
                <RequiredDocumentsTab source="FRONT">
                    {/* Row Button Action to change status by prescriber on invalid dossier status */}
                    <RequiredDocumentsStatusAction />
                </RequiredDocumentsTab>
            ),
        },
        {
            tabName: 'downloadable-document',
            tabTitle: (
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    Documents remis{' '}
                    {downloadableDocuments > 0 && (
                        <CountBadge
                            count={downloadableDocuments}
                            color="primary"
                            bgVariant="light"
                        />
                    )}
                </Box>
            ),
            tabPanel: <DownloadableDocumentsTab source="FRONT" />,
        },
        {
            tabName: 'billing',
            tabTitle: 'Facturation',
            tabPanel: <BillingTab />,
        },
        {
            tabName: 'message',
            tabTitle: (
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    Messages{' '}
                    <CountBadge
                        count={
                            topicId && unreadCountByTopic[topicId] ? unreadCountByTopic[topicId] : 0
                        }
                        color="primary"
                        bgVariant="light"
                    />
                </Box>
            ),
            tabPanel: <MessageTab />,
        },
        {
            tabName: 'signatures-electronique',
            tabTitle: 'Signature électronique',
            tabPanel: (
                <>
                    <DocumentSignTab />
                    <DossierSignDetailOverlay />
                </>
            ),
        },
        {
            tabName: 'historique',
            tabTitle: 'Historique',
            tabPanel: (
                <ActivityTab
                    showTask={false}
                    showTechniqueLogs={false}
                    noteMemo={false}
                    showNotes={false}
                />
            ),
        },
    ];

    return (
        <FlexyTabs
            routerParameterName="dossier-detail"
            sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
            tabs={tabs}
            scrollbar={false}
            indicatorColor="primary"
        />
    );
};

export default DossierBody;
